import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { MedicationFinderService } from '@services/medication-finder.service';
import { MedicationSavings } from '@interfaces/medication-savings.interface';
import { SubscriptionManager } from '@zelis/platform-ui-components';
import { Subscription } from 'rxjs';
import { ProductAnalyticsService } from '@services/product-analytics/product-analytics.service';
import { DecimalPipe } from '@angular/common';

@Component({
  selector: 'app-medication-finder-savings',
  templateUrl: './medication-finder-savings.component.html',
  styleUrls: ['./medication-finder-savings.component.scss'],
})
export class MedicationFinderSavingsComponent implements OnInit, OnDestroy {
  @Input() public showHeader?: boolean = true;
  @Input() public contentSpacing?: boolean = true;
  @Input() public fullscreen?: boolean = false;

  public variant: string = 'elevated';
  public emphasis: string = 'high';
  public iconName: string = 'dls-illustration-medication';
  public totalSavings: number;
  public title: string;
  public cardText: string;
  public buttonText: string = 'medication_finder_savings_button_text';
  public isRewardable: boolean;

  private subscriptions: SubscriptionManager = new SubscriptionManager();
  private suppressTrackEvent: boolean = false;
  private pendoMedFinderSavingsData = {
    reward_and_savings_presented: false,
    reward_only_presented: false,
    savings_only_presented: false,
    reward_amount: null,
    savings_amount: null,
    view_savings_button_clicked: false,
  };

  constructor(
    private medicationFinderService: MedicationFinderService,
    private pendoService: ProductAnalyticsService,
    private numberPipe: DecimalPipe
  ) {}

  ngOnInit(): void {
    this.subscriptions.add(this.getMemberSavings());
  }

  ngOnDestroy(): void {
    this.subscriptions.destroy();
  }

  public goToRxSS(): void {
    this.sendPendoTrackEvent(true);
    this.medicationFinderService.redirectToRxss();
  }

  private getMemberSavings(): Subscription {
    return this.medicationFinderService
      .getSavings()
      .subscribe((savings: MedicationSavings) => {
        this.totalSavings = savings ? savings.total_member_savings : 0;
        this.isRewardable = savings && !!savings.is_rewardable;
        this.determineTitleAndText();
        this.sendPendoTrackEvent();
      });
  }

  private determineTitleAndText(): void {
    if (this.totalSavings > 0 && this.isRewardable) {
      this.title = 'medication_finder_savings_and_rewards_card_title';
      this.cardText = 'medication_finder_savings_and_rewards_card_text';
      this.setPendoMedFinderSavingsData(1);
    } else if (this.totalSavings > 0) {
      this.title = 'medication_finder_savings_card_title';
      this.cardText = 'medication_finder_savings_card_text';
      this.setPendoMedFinderSavingsData(2);
    } else if (this.isRewardable) {
      this.title = 'medication_finder_rewards_card_title';
      this.cardText = 'medication_finder_rewards_card_text';
      this.buttonText = 'medication_finder_rewards_button_text';
      this.setPendoMedFinderSavingsData(3);
    } else {
      this.title = 'medication_finder_savings_card_title_no_savings';
      this.cardText = 'medication_finder_savings_card_text';
      this.suppressTrackEvent = true;
    }
  }

  private setPendoMedFinderSavingsData(option: number) {
    let updatedValues = {};
    switch (option) {
      case 1:
        updatedValues = {
          reward_and_savings_presented: true,
          reward_amount: '$100',
          savings_amount:
            '$' + this.numberPipe.transform(this.totalSavings, '1.2-2'),
        };
        break;
      case 2:
        updatedValues = {
          savings_only_presented: true,
          savings_amount:
            '$' + this.numberPipe.transform(this.totalSavings, '1.2-2'),
        };
        break;
      case 3:
        updatedValues = {
          reward_only_presented: true,
          reward_amount: '$100',
        };
        break;
    }

    this.pendoMedFinderSavingsData = {
      ...this.pendoMedFinderSavingsData,
      ...updatedValues,
    };
  }

  private sendPendoTrackEvent(clickEvent = false): void {
    if (this.suppressTrackEvent) {
      return;
    }
    const eventName = 'MedFinder RX Rewards/ Savings Activation';
    const pendoData = {
      ...this.pendoMedFinderSavingsData,
      view_savings_button_clicked: clickEvent,
    };
    this.pendoService.sendTrackEvent(eventName, pendoData);
  }
}
